<template>
  <div
    class="flex flex-col gap-5 py-6 rounded-lg bg-utility-blue-50 border-sm border-utility-blue-200 border-opacity-100 shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]"
  >
    <div class="flex gap-2 justify-between items-start px-6">
      <div>
        <p class="text-utility-blue-700 text-lg font-semibold">
          {{ t('AKI Prediction Service') }}
        </p>
        <p class="text-subtitle text-sm">{{ t('Information about the backend service') }}</p>
      </div>
      <button data-testid="btn-close" @click="$emit('close-sidebar')">
        <VIcon color="tree-node" icon="close" size="20" />
      </button>
    </div>
    <div
      class="flex flex-col gap-5 flex-1 overflow-y-auto min-h-0 px-6 text-subtitle text-sm pb-[1px]"
    >
      <p v-if="!hasAcceptedAkiLimitation">
        {{
          t(
            'This backend service is intended to be used by healthcare professionals that have undergone medical school or nursing programs, and that have completed an introductory tutorial for this backend service. Ensure that you are also familiar with the Instructions for Use of this backend service.'
          )
        }}
      </p>
      <template v-else>
        <p v-if="!udiData && !isLoadingUdiData" class="text-[#D92D20]">
          {{
            t(
              'Loading of elements has failed. See below for details. Contact info@tcc-clinicalsolutions.de or check the instruction for use for further MDR information.'
            )
          }}
        </p>
        <div>
          <div class="flex flex-col gap-2">
            <p class="font-semibold">{{ t('Risk Category Reference Range:') }}</p>
            <div class="flex flex-col">
              <p
                v-for="category in riskCategories"
                :key="category.label"
                class="grid items-center gap-2"
                style="grid-template-columns: 4.5em auto"
              >
                <span class="text-right tabular-nums">{{ category.range }}</span>
                <span class="flex items-center gap-2">
                  {{ t(category.label) }}
                  <span
                    v-if="category.color"
                    class="inline-block w-2 h-2 rounded-full shrink-0"
                    :style="{ backgroundColor: category.color }"
                  />
                </span>
              </p>
            </div>
          </div>
        </div>
        <div>
          <p class="font-semibold">{{ t('Explanation') }}</p>
          <p>
            {{
              t(
                'The backend service calculates the probability of developing acute kidney injury (AKI) within the next 10 hours based on available patient information. The prediction is based on the KDIGO criterion of an increase in serum creatinine of at least 0.3 mg/dL within 48 hours.'
              )
            }}
          </p>
          <p>
            {{
              t(
                'To generate the prediction, the backend service uses demographic data, vital signs, medication data, and laboratory values. The parameters used include age, body mass index (BMI), creatinine, lactate, pH, C-reactive protein (CRP), potassium, bilirubin, leukocyte count, oxygen saturation (SpO₂), mean arterial pressure (MAP), heart rate, and information on vasopressor administration.'
              )
            }}
          </p>
          <p>
            {{
              t(
                'Creatinine is a mandatory laboratory parameter. In addition, a maximum of five further input parameters may be missing. Predictions are generated exclusively for adult patients whose serum creatinine was previously within the normal range (≤ 1.44 mg/dL) and for whom no prior renal replacement therapy has been documented.'
              )
            }}
          </p>
        </div>
        <div>
          <p class="font-semibold">{{ t('Important') }}</p>
          <p>
            {{
              t(
                'The displayed results are intended solely to support healthcare professionals and do not replace clinical assessment, diagnostic procedures, or laboratory analyses.'
              )
            }}
          </p>

          <p>
            {{
              t(
                "The backend service does not provide a diagnosis and does not recommend treatment. Clinical decisions must not be based solely on the results of this backend service. Any clinical deterioration or uncertainty regarding a patient's condition should prompt a timely clinical assessment, regardless of the result provided by the backend service."
              )
            }}
          </p>
        </div>
      </template>
      <div>
        <p class="font-semibold">{{ t('Contraindications') }}</p>
        <p>{{ t('Do not use the backend service for the following patient groups:') }}</p>
        <ul class="list-disc list-inside mt-2 ml-1.5">
          <li>{{ t('Children under the age of 18') }}</li>
          <li>{{ t('Patients with previously documented renal replacement therapy') }}</li>
          <li>{{ t('Patients with a previously diagnosed Acute Kidney Injury (AKI)') }}</li>
          <li>
            {{
              t(
                'Patients receiving medication that may significantly affect the input parameters relevant to the algorithm'
              )
            }}
          </li>
        </ul>
        <p class="mt-2">
          {{
            t(
              'The results of this backend service must always be interpreted within the overall clinical context of the individual patient.'
            )
          }}
        </p>
      </div>
      <div v-if="hasAcceptedAkiLimitation && !isLoadingUdiData" class="flex flex-col gap-5">
        <UdiItem
          v-for="item in udiItems"
          :key="item.key"
          :udi-key="item.key"
          :text="item.text"
          :is-error="item.isError"
        />
      </div>
      <VForm
        ref="formRef"
        data-testid="consent-form"
        v-else
        class="flex flex-col gap-10"
        @submit.prevent="handleConfirm"
      >
        <VCheckbox
          data-testid="consent-checkbox"
          v-model="consent"
          :label="t('I have read and understood all limitations and contraindications.')"
          :rules="[consentRule]"
          class="aki-consent-checkbox"
          density="compact"
          hide-details="auto"
          base-color="border-primary"
        />
        <VBtn
          data-testid="confirm-button"
          color="primary"
          class="button-large self-end w-fit !h-10"
          :loading="isConsentAcceptanceLoading"
          type="submit"
        >
          {{ t('Accept and show algorithm') }}
        </VBtn>
      </VForm>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAkiPlugin } from '@/composables/useAkiPlugin'
import { AkiPredictionsService } from '@/services/telehealthApi/AkiPredictionsService'
import { UserSettingsService } from '@/services/telehealthApi/UserSettingsService'
import { NoticeType } from '@/stores/models/Notice'
import { useNoticeStore } from '@/stores/notice'
import { computed, onMounted, ref } from 'vue'
import { PredictionResultDTOProbabilityColorCodeEnum, type PluginUdiDto } from '@/generated'
import { useI18n } from 'vue-i18n'
import { VBtn, VCheckbox, VForm } from 'vuetify/components'
import { storeToRefs } from 'pinia'
import { format, isValid, parse } from 'date-fns'
import { DateFormatter } from '@/enums/DateFormatter'
import UdiItem from '../atoms/UdiItem.vue'
import { useAkiPredictionsStore } from '@/stores/akiPredictions'

defineEmits<{
  (e: 'close-sidebar'): void
}>()

const { t } = useI18n()
const noticeStore = useNoticeStore()
const { setUdiData, getAkiPredictionsForUser } = useAkiPredictionsStore()
const {
  hasAcceptedAkiLimitation,
  REQUIRED_AKI_LIMITATIONS_SCOPE,
  REQUIRED_AKI_LIMITATIONS_VERSION
} = useAkiPlugin()

// Hardcoded to the color codes the backend currently exposes on prediction results
// (PredictionResultDTOProbabilityColorCodeEnum); no dedicated per-category endpoint exists yet.
const riskCategories = [
  { range: '0–32', label: 'No Risk Category', color: null },
  { range: '33–42', label: 'Medium Risk', color: null },
  { range: '43–76', label: 'High Risk', color: PredictionResultDTOProbabilityColorCodeEnum.FF9900 },
  {
    range: '77–100',
    label: 'Very High Risk',
    color: PredictionResultDTOProbabilityColorCodeEnum.E71010
  }
]

const { udiData } = storeToRefs(useAkiPredictionsStore())
const formRef = ref<InstanceType<typeof VForm>>()
const consent = ref<boolean>(false)
const isConsentAcceptanceLoading = ref<boolean>(false)
const isLoadingUdiData = ref(false)

onMounted(async () => {
  if (hasAcceptedAkiLimitation.value && !udiData.value) {
    await fetchUdiData()
  }
})

const fetchUdiData = async () => {
  if (isLoadingUdiData.value) return

  isLoadingUdiData.value = true
  try {
    const udiResponse = await AkiPredictionsService.getUdi()
    setUdiData(udiResponse)
  } catch (e) {
    console.error('Failed to fetch UDI data: ', e)
    noticeStore.add({
      type: NoticeType.ERROR,
      message: t('Failed to fetch UDI data')
    })
    setUdiData(null)
  } finally {
    isLoadingUdiData.value = false
  }
}

const udiKeys: (keyof PluginUdiDto)[] = [
  'ceMark',
  'instructionsForUse',
  'caution',
  'type',
  'productName',
  'dateOfManufacture',
  'manufacturer',
  'udi',
  'labelIdentification'
]

const udiItems = computed(() => {
  const isError = !udiData.value

  return udiKeys.map((key) => {
    const formattedText = (() => {
      if (key === 'manufacturer') {
        return [udiData.value?.manufacturer, udiData.value?.address].filter(Boolean).join('\n')
      }
      if (key === 'udi') {
        return udiData.value?.udi?.match(/(\([^)]+\)[^(]+)/g) ?? udiData?.value?.udi
      }
      if (key === 'dateOfManufacture') {
        const parsedDate = parse(udiData.value?.dateOfManufacture || '', 'yyMMdd', new Date())
        return isValid(parsedDate)
          ? format(parsedDate, DateFormatter.DATE_FULL_YEAR)
          : udiData.value?.dateOfManufacture
      }
      return udiData.value?.[key]
    })()

    return {
      key,
      text: formattedText,
      isError
    }
  })
})

const consentRule = (v: boolean) =>
  v || t('Accepting these terms is mandatory before you can continue.')

const handleConfirm = async () => {
  if (isConsentAcceptanceLoading.value) return

  const { valid } = await formRef.value!.validate()
  if (!valid) return

  isConsentAcceptanceLoading.value = true

  try {
    await AkiPredictionsService.acceptAkiLimitation({
      version: REQUIRED_AKI_LIMITATIONS_VERSION,
      scope: REQUIRED_AKI_LIMITATIONS_SCOPE
    })
  } catch (error) {
    noticeStore.add({
      type: NoticeType.ERROR,
      message: t('Failed to accept AKI terms. Please try again later.')
    })
    isConsentAcceptanceLoading.value = false
    return
  }

  try {
    const isSynced = await UserSettingsService.syncUserWithBackend()

    if (!isSynced) {
      throw new Error('Sync failed')
    }

    noticeStore.add({
      type: NoticeType.SUCCESS,
      message: t('AKI terms accepted')
    })
  } catch (error) {
    noticeStore.add({
      type: NoticeType.WARNING,
      message: t('Terms accepted, but failed to sync user data. Please refresh the page.')
    })
  } finally {
    isConsentAcceptanceLoading.value = false
  }

  // AKI predictions are only fetched once the contraindications are accepted, so
  // trigger the initial load here (mirrors EdsSidebar re-fetching the patient list).
  await Promise.all([fetchUdiData(), getAkiPredictionsForUser()])
}
</script>
<style scoped>
.aki-consent-checkbox :deep(.v-selection-control) {
  align-items: flex-start;
  gap: 4px;
}

.aki-consent-checkbox :deep(.v-label) {
  font-size: 14px;
  color: #414651;
  font-weight: 600;
}

.aki-consent-checkbox :deep(.v-input__details) {
  padding-inline-start: 32px;
}

.aki-consent-checkbox.v-input--error :deep(.v-messages) {
  color: #d92d20;
}

.aki-consent-checkbox.v-input--error :deep(.v-selection-control__input > .v-icon) {
  color: #f04438;
}
</style>
